import { useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import {
  DollarSign,
  TrendingUp,
  AlertTriangle,
  PieChart,
  RefreshCw,
  AlertCircle,
  ArrowUpRight,
  ArrowDownRight,
  Activity,
} from 'lucide-react';
import { Card, CardBody, CardHeader, CardTitle, Badge, Button, Select } from '../../components/ui';
import { api } from '../../lib/api';
import { ENDPOINTS } from '../../config/api';

interface DashboardSummary {
  totalRevenue: number;
  totalCosts: number;
  totalMargin: number;
  marginPercentage: number;
  operationsCount: number;
  discrepancyCount: number;
  feesAtRiskCount: number;
}

interface MarginByOperation {
  operationType: string;
  count: number;
  grossAmount: number;
  feesCharged: number;
  realCost: number;
  margin: number;
  marginPercentage: number;
}

interface FeeAtRisk {
  tenantId: string;
  tenantName: string;
  operationType: string;
  currentMargin: number;
  realCost: number;
  configuredFee: number;
}

export function AdminCostsDashboard() {
  const [period, setPeriod] = useState('30d');

  const { data: summary, isLoading: summaryLoading, error: summaryError, refetch: refetchSummary } = useQuery({
    queryKey: ['admin-dashboard-summary', period],
    queryFn: async () => {
      // Calculate date range based on period
      const endDate = new Date();
      const startDate = new Date();
      switch (period) {
        case '7d':
          startDate.setDate(startDate.getDate() - 7);
          break;
        case '30d':
          startDate.setDate(startDate.getDate() - 30);
          break;
        case '90d':
          startDate.setDate(startDate.getDate() - 90);
          break;
        case '365d':
          startDate.setDate(startDate.getDate() - 365);
          break;
      }
      
      // Get revenue data
      const revenueResponse = await api.get(ENDPOINTS.adminDashboard.revenue, { 
        params: { 
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString()
        } 
      });
      const revenue = revenueResponse.data;
      
      // Get reconciliation status
      const reconciliationResponse = await api.get(ENDPOINTS.adminDashboard.reconciliation);
      const reconciliation = reconciliationResponse.data;
      
      // Get fees at risk count
      let feesAtRiskCount = 0;
      try {
        const feesAtRiskResponse = await api.get(ENDPOINTS.adminDashboard.feesAtRisk);
        feesAtRiskCount = Array.isArray(feesAtRiskResponse.data) ? feesAtRiskResponse.data.length : 0;
      } catch {
        // Ignore if endpoint doesn't exist
      }
      
      return {
        totalRevenue: revenue.totals?.feesCharged || 0,
        totalCosts: revenue.totals?.realCost || 0,
        totalMargin: revenue.totals?.platformMargin || 0,
        marginPercentage: revenue.totals?.grossAmount > 0 
          ? (revenue.totals?.platformMargin / revenue.totals?.grossAmount) * 100 
          : 0,
        operationsCount: revenue.totals?.operationCount || 0,
        discrepancyCount: reconciliation.discrepancies || 0,
        feesAtRiskCount,
      } as DashboardSummary;
    },
  });

  const { data: margins, isLoading: marginsLoading } = useQuery({
    queryKey: ['admin-dashboard-margins', period],
    queryFn: async () => {
      // Calculate date range based on period
      const endDate = new Date();
      const startDate = new Date();
      switch (period) {
        case '7d':
          startDate.setDate(startDate.getDate() - 7);
          break;
        case '30d':
          startDate.setDate(startDate.getDate() - 30);
          break;
        case '90d':
          startDate.setDate(startDate.getDate() - 90);
          break;
        case '365d':
          startDate.setDate(startDate.getDate() - 365);
          break;
      }
      
      const response = await api.get(ENDPOINTS.adminDashboard.margins, { 
        params: { 
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString()
        } 
      });
      
      // Handle both array and object formats for byOperationType
      const byOperationType = response.data?.byOperationType;
      if (Array.isArray(byOperationType)) {
        return byOperationType as MarginByOperation[];
      }
      // If it's an object, transform to array
      if (byOperationType && typeof byOperationType === 'object') {
        return Object.entries(byOperationType).map(([operationType, data]: [string, any]) => ({
          operationType,
          count: data.count || 0,
          grossAmount: data.grossAmount || 0,
          feesCharged: data.feesCharged || 0,
          realCost: data.realCost || 0,
          margin: data.margin || 0,
          marginPercentage: data.marginPercentage || (data.grossAmount > 0 ? (data.margin / data.grossAmount) * 100 : 0),
        })) as MarginByOperation[];
      }
      return [] as MarginByOperation[];
    },
  });

  const { data: feesAtRisk, isLoading: feesAtRiskLoading } = useQuery({
    queryKey: ['admin-dashboard-fees-at-risk'],
    queryFn: async () => {
      const response = await api.get(ENDPOINTS.adminDashboard.feesAtRisk);
      return response.data as FeeAtRisk[];
    },
  });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value / 100);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(2)}%`;
  };

  if (summaryError) {
    return (
      <div className="flex flex-col items-center justify-center h-96 gap-4">
        <AlertCircle className="w-16 h-16 text-red-400" />
        <h2 className="text-xl font-semibold text-surface-100">Erro ao carregar dashboard</h2>
        <Button variant="primary" onClick={() => refetchSummary()}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Tentar novamente
        </Button>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-surface-100">Dashboard de Custos e Receitas</h1>
          <p className="text-surface-400">Visão consolidada de margens, custos e taxas</p>
        </div>
        <div className="flex items-center gap-4">
          <Select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            options={[
              { value: '7d', label: 'Últimos 7 dias' },
              { value: '30d', label: 'Últimos 30 dias' },
              { value: '90d', label: 'Últimos 90 dias' },
              { value: '365d', label: 'Último ano' },
            ]}
          />
          <Button variant="secondary" onClick={() => refetchSummary()} disabled={summaryLoading}>
            <RefreshCw className={`w-4 h-4 ${summaryLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card variant="stat">
          <CardBody>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-surface-400 text-sm">Receita Total</p>
                {summaryLoading ? (
                  <div className="h-8 w-32 bg-surface-700 animate-pulse rounded mt-1" />
                ) : (
                  <p className="text-2xl font-bold text-surface-100">
                    {formatCurrency(summary?.totalRevenue || 0)}
                  </p>
                )}
              </div>
              <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-green-400" />
              </div>
            </div>
            <div className="flex items-center gap-1 mt-2 text-sm text-green-400">
              <ArrowUpRight className="w-4 h-4" />
              <span>{summary?.operationsCount || 0} operações</span>
            </div>
          </CardBody>
        </Card>

        <Card variant="stat">
          <CardBody>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-surface-400 text-sm">Custos Totais</p>
                {summaryLoading ? (
                  <div className="h-8 w-32 bg-surface-700 animate-pulse rounded mt-1" />
                ) : (
                  <p className="text-2xl font-bold text-surface-100">
                    {formatCurrency(summary?.totalCosts || 0)}
                  </p>
                )}
              </div>
              <div className="w-12 h-12 rounded-xl bg-red-500/20 flex items-center justify-center">
                <ArrowDownRight className="w-6 h-6 text-red-400" />
              </div>
            </div>
            <div className="flex items-center gap-1 mt-2 text-sm text-surface-400">
              <Activity className="w-4 h-4" />
              <span>Provider + Parceiros + Gerentes</span>
            </div>
          </CardBody>
        </Card>

        <Card variant="stat">
          <CardBody>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-surface-400 text-sm">Margem Líquida</p>
                {summaryLoading ? (
                  <div className="h-8 w-32 bg-surface-700 animate-pulse rounded mt-1" />
                ) : (
                  <p className="text-2xl font-bold text-surface-100">
                    {formatCurrency(summary?.totalMargin || 0)}
                  </p>
                )}
              </div>
              <div className="w-12 h-12 rounded-xl bg-primary-500/20 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-primary-400" />
              </div>
            </div>
            <div className="flex items-center gap-1 mt-2 text-sm text-primary-400">
              <PieChart className="w-4 h-4" />
              <span>{formatPercentage(summary?.marginPercentage || 0)} de margem</span>
            </div>
          </CardBody>
        </Card>

        <Card variant="stat">
          <CardBody>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-surface-400 text-sm">Alertas</p>
                {summaryLoading ? (
                  <div className="h-8 w-32 bg-surface-700 animate-pulse rounded mt-1" />
                ) : (
                  <p className="text-2xl font-bold text-surface-100">
                    {(summary?.discrepancyCount || 0) + (summary?.feesAtRiskCount || 0)}
                  </p>
                )}
              </div>
              <div className="w-12 h-12 rounded-xl bg-yellow-500/20 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-yellow-400" />
              </div>
            </div>
            <div className="flex items-center gap-2 mt-2 text-sm">
              <Badge variant="warning">{summary?.feesAtRiskCount || 0} taxas em risco</Badge>
              <Badge variant="error">{summary?.discrepancyCount || 0} discrepâncias</Badge>
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Margins by Operation */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PieChart className="w-5 h-5 text-primary-400" />
            Margens por Tipo de Operação
          </CardTitle>
        </CardHeader>
        <CardBody className="p-0">
          {marginsLoading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="animate-pulse bg-surface-800 rounded h-16" />
              ))}
            </div>
          ) : !margins || margins.length === 0 ? (
            <div className="text-center py-12 text-surface-400">
              <PieChart className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Nenhuma operação no período</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-surface-800/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-surface-400 uppercase">Operação</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-surface-400 uppercase">Qtd</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-surface-400 uppercase">Volume</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-surface-400 uppercase">Taxas</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-surface-400 uppercase">Custo Real</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-surface-400 uppercase">Margem</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-surface-400 uppercase">%</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-surface-800">
                  {margins.map((item) => (
                    <tr key={item.operationType} className="hover:bg-surface-800/30">
                      <td className="px-6 py-4 text-surface-100 font-medium">{item.operationType}</td>
                      <td className="px-6 py-4 text-right text-surface-300">{item.count}</td>
                      <td className="px-6 py-4 text-right text-surface-300">{formatCurrency(item.grossAmount)}</td>
                      <td className="px-6 py-4 text-right text-green-400">{formatCurrency(item.feesCharged)}</td>
                      <td className="px-6 py-4 text-right text-red-400">{formatCurrency(item.realCost)}</td>
                      <td className="px-6 py-4 text-right text-primary-400 font-medium">{formatCurrency(item.margin)}</td>
                      <td className="px-6 py-4 text-right">
                        <Badge variant={item.marginPercentage >= 10 ? 'success' : item.marginPercentage >= 5 ? 'warning' : 'error'}>
                          {formatPercentage(item.marginPercentage)}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Fees at Risk */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-yellow-400" />
            Taxas em Risco
            {feesAtRisk && feesAtRisk.length > 0 && (
              <Badge variant="warning">{feesAtRisk.length}</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardBody className="p-0">
          {feesAtRiskLoading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="animate-pulse bg-surface-800 rounded h-16" />
              ))}
            </div>
          ) : !feesAtRisk || feesAtRisk.length === 0 ? (
            <div className="text-center py-12 text-surface-400">
              <AlertTriangle className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Nenhuma taxa em risco</p>
              <p className="text-sm mt-1">Todas as taxas estão acima do custo real</p>
            </div>
          ) : (
            <div className="divide-y divide-surface-800">
              {feesAtRisk.map((fee, index) => (
                <div
                  key={`${fee.tenantId}-${fee.operationType}-${index}`}
                  className="flex items-center justify-between px-6 py-4 hover:bg-surface-800/30"
                >
                  <div>
                    <p className="font-medium text-surface-100">{fee.tenantName}</p>
                    <p className="text-sm text-surface-500">{fee.operationType}</p>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <p className="text-sm text-surface-400">Taxa Configurada</p>
                      <p className="text-surface-100">{formatCurrency(fee.configuredFee)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-surface-400">Custo Real</p>
                      <p className="text-red-400">{formatCurrency(fee.realCost)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-surface-400">Margem</p>
                      <Badge variant={fee.currentMargin >= 0 ? 'warning' : 'error'}>
                        {formatCurrency(fee.currentMargin)}
                      </Badge>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardBody>
      </Card>
    </motion.div>
  );
}
